import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, fmtMoney } from "@/lib/api";
import { Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Drill-down sheet that shows the General Ledger for a given account between
 * date_from and date_to. Used by P&L and Balance Sheet to let users click an
 * account row and inspect the underlying journal lines (Zoho-style parity).
 *
 * Props:
 *   open, onOpenChange
 *   accountId   — required
 *   accountName — display title
 *   dateFrom, dateTo — period (BS uses 1900-01-01 → as_of)
 */
export default function LedgerDrillDown({ open, onOpenChange, accountId, accountName, dateFrom, dateTo }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !accountId) return;
        setLoading(true);
        setData(null);
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        api.get(`/general-ledger`, { params: { account_id: accountId, ...params } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    }, [open, accountId, dateFrom, dateTo]);

    const totalDebit = (data?.rows || []).reduce((s, r) => s + (r.debit || 0), 0);
    const totalCredit = (data?.rows || []).reduce((s, r) => s + (r.credit || 0), 0);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-[860px] overflow-y-auto p-0"
                data-testid="ledger-drilldown"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border sticky top-0 bg-background z-10">
                    <div className="flex items-baseline justify-between gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">Account Transactions</div>
                            <SheetTitle className="text-xl font-display font-light tracking-tight mt-0.5" data-testid="drilldown-account-name">
                                {accountName || data?.account?.name || "—"}
                            </SheetTitle>
                            <SheetDescription className="sr-only">General ledger transactions for this account in the selected period.</SheetDescription>
                            {data?.account && (
                                <div className="text-[12px] text-muted-foreground font-mono mt-0.5">
                                    {data.account.code} · {data.account.type}
                                </div>
                            )}
                        </div>
                        {(dateFrom || dateTo) && (
                            <div className="text-[11px] text-muted-foreground text-right">
                                {dateFrom && <>From {dateFrom}<br /></>}
                                {dateTo && <>To {dateTo}</>}
                            </div>
                        )}
                    </div>
                </SheetHeader>

                <div className="px-6 py-5">
                    {loading && (
                        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading ledger…
                        </div>
                    )}

                    {!loading && data && (
                        <>
                            {data.rows.length === 0 ? (
                                <div className="text-center text-muted-foreground py-12 text-sm" data-testid="drilldown-empty">
                                    No transactions in this period.
                                </div>
                            ) : (
                                <div className="border border-border rounded-md overflow-hidden">
                                    <table className="w-full text-[12.5px]">
                                        <thead className="bg-muted/40 border-b border-border">
                                            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                                <th className="px-3 py-2.5">Date</th>
                                                <th className="px-3 py-2.5">Journal</th>
                                                <th className="px-3 py-2.5">Narration</th>
                                                <th className="px-3 py-2.5 text-right">Debit</th>
                                                <th className="px-3 py-2.5 text-right">Credit</th>
                                                <th className="px-3 py-2.5 text-right">Balance</th>
                                                <th className="px-3 py-2.5 w-8"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.rows.map((r, i) => (
                                                <tr
                                                    key={`${r.journal_number}-${i}`}
                                                    className="border-b border-border last:border-0 hover:bg-muted/30"
                                                    data-testid={`drilldown-row-${i}`}
                                                >
                                                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[11.5px]">{r.date}</td>
                                                    <td className="px-3 py-2 font-mono text-[11.5px]">{r.journal_number}</td>
                                                    <td className="px-3 py-2 text-muted-foreground">{r.narration || r.reference || "—"}</td>
                                                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.debit ? fmtMoney(r.debit) : "—"}</td>
                                                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.credit ? fmtMoney(r.credit) : "—"}</td>
                                                    <td className="px-3 py-2 text-right font-mono tabular-nums font-medium">{fmtMoney(r.balance)}</td>
                                                    <td className="px-3 py-2">
                                                        <SourceLink source={r.source} sourceId={r.source_id} onClose={() => onOpenChange(false)} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-muted/30 border-t-2 border-foreground">
                                            <tr className="font-semibold">
                                                <td className="px-3 py-2.5" colSpan={3}>Totals ({data.rows.length} entr{data.rows.length === 1 ? "y" : "ies"})</td>
                                                <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="drilldown-total-debit">{fmtMoney(totalDebit)}</td>
                                                <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="drilldown-total-credit">{fmtMoney(totalCredit)}</td>
                                                <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="drilldown-closing-balance">{fmtMoney(data.closing_balance)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

const SOURCE_PATHS = {
    invoice: "/sales/invoices/",
    bill: "/purchases/bills/",
    payment_received: "/sales/payments/",
    payment_made: "/purchases/payments/",
    journal: "/accounting/journals",
    bank_transaction: "/banking/reconciliation",
};

function SourceLink({ source, sourceId, onClose }) {
    if (!source) return null;
    const base = SOURCE_PATHS[source];
    if (!base) return null;
    const to = sourceId && (source === "invoice" || source === "bill") ? `${base}${sourceId}` : base;
    return (
        <Link
            to={to}
            onClick={onClose}
            className="text-muted-foreground hover:text-gold inline-flex items-center"
            title={`Open source ${source}`}
            data-testid={`drilldown-source-link-${source}`}
        >
            <ExternalLink className="w-3.5 h-3.5" />
        </Link>
    );
}
